import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Lock, AlertCircle, CheckCircle, Eye, EyeOff, XCircle } from 'lucide-react';
import axios from 'axios';
import HireHubLogo from '../components/HireHubLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [isGuestCheckout, setIsGuestCheckout] = useState(false); // P1 FIX: Track if this is guest checkout flow

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError('No reset token provided');
        setVerifying(false);
        return;
      }

      try {
        await axios.post(`${API_URL}/api/auth/verify-reset-token`, { token });
        setTokenValid(true);
      } catch (err) {
        setTokenError(err.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { strength: 33, label: 'Weak', color: 'bg-red-500' };
    if (score <= 4) return { strength: 66, label: 'Medium', color: 'bg-yellow-500' };
    return { strength: 100, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: formData.password
      });
      
      // P1 FIX: Check for booking context to continue guest checkout flow
      if (response.data.booking_context) {
        const context = response.data.booking_context;
        // Store booking context for verification flow
        sessionStorage.setItem('pendingBookingContext', JSON.stringify(context));
        
        // Mark this as guest checkout flow for success UI
        setIsGuestCheckout(true);
        setSuccess(true);
        
        // After short delay, redirect to verification with vehicle context
        setTimeout(() => {
          const vehicleId = context.vehicle_id;
          if (vehicleId) {
            navigate(`/verification?from=/vehicles/${vehicleId}`, { replace: true });
          } else {
            navigate('/verification', { replace: true });
          }
        }, 2500);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-[#004B8D] mx-auto mb-4"></div>
          <p className="text-slate-500">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center mb-8">
            <HireHubLogo size="lg" />
          </Link>

          <Card className="border-slate-100 shadow-lg rounded-2xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="font-heading text-2xl">Link Expired</CardTitle>
              <CardDescription className="text-base">
                {tokenError || 'This password reset link is invalid or has expired.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <Link to="/forgot-password">
                <Button className="w-full bg-[#004B8D] hover:bg-[#003865] h-12 rounded-xl font-medium">
                  Request New Reset Link
                </Button>
              </Link>
              
              <p className="text-center text-sm text-slate-600 mt-4">
                <Link to="/login" className="text-[#004B8D] font-medium hover:underline">
                  Back to Sign In
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center mb-8">
            <HireHubLogo size="lg" />
          </Link>

          <Card className="border-slate-100 shadow-lg rounded-2xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="font-heading text-2xl">
                {isGuestCheckout ? 'Password Set!' : 'Password Reset!'}
              </CardTitle>
              <CardDescription className="text-base">
                {isGuestCheckout 
                  ? 'Your password has been set successfully. Redirecting you to complete verification...'
                  : 'Your password has been successfully reset. You can now sign in with your new password.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {isGuestCheckout ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-2 text-[#004B8D]">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#004B8D] border-t-transparent" />
                    <span className="text-sm font-medium">Redirecting to verification...</span>
                  </div>
                  <p className="text-xs text-slate-500 text-center">
                    You'll be taken to verify your identity and continue with your booking.
                  </p>
                </div>
              ) : (
                <Link to="/login">
                  <Button className="w-full bg-[#004B8D] hover:bg-[#003865] h-12 rounded-xl font-medium">
                    Sign In
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <HireHubLogo size="lg" />
        </Link>

        <Card className="border-slate-100 shadow-lg rounded-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-2xl">Create new password</CardTitle>
            <CardDescription>
              Enter a new password for your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    className="pl-10 pr-10 h-12 rounded-lg"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password strength indicator */}
                {formData.password && (
                  <div className="mt-2 space-y-1">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                    <p className={`text-xs ${
                      passwordStrength.label === 'Weak' ? 'text-red-500' :
                      passwordStrength.label === 'Medium' ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      Password strength: {passwordStrength.label}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    className="pl-10 pr-10 h-12 rounded-lg"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Passwords match
                  </p>
                )}
              </div>

              {/* Password requirements */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p className="font-medium mb-2 text-slate-700">Password requirements:</p>
                <ul className="space-y-1 text-xs">
                  <li className={`flex items-center gap-2 ${formData.password.length >= 8 ? 'text-green-600' : 'text-slate-400'}`}>
                    {formData.password.length >= 8 ? <CheckCircle className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current" />}
                    At least 8 characters
                  </li>
                  <li className={`flex items-center gap-2 ${formData.password === formData.confirmPassword && formData.confirmPassword ? 'text-green-600' : 'text-slate-400'}`}>
                    {formData.password === formData.confirmPassword && formData.confirmPassword ? <CheckCircle className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current" />}
                    Passwords match
                  </li>
                </ul>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#004B8D] hover:bg-[#003865] h-12 rounded-xl font-medium"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
